import React, { useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { FileText, X, Plus, Trash2, Loader2 } from "lucide-react";

/** Default major-destinations preset — matches the user's printable rate sheet. */
const DEFAULT_ROUTES = [
  { label: "Downtown Milwaukee → MKE Airport",            pickup_address: "Downtown Milwaukee, WI",                                      dropoff_address: "Milwaukee Mitchell International Airport (MKE), Milwaukee, WI" },
  { label: "Downtown Milwaukee → ORD Chicago Airport",    pickup_address: "Downtown Milwaukee, WI",                                      dropoff_address: "O'Hare International Airport (ORD), Chicago, IL" },
  { label: "Downtown Milwaukee → MSN Madison Airport",    pickup_address: "Downtown Milwaukee, WI",                                      dropoff_address: "Dane County Regional Airport (MSN), Madison, WI" },
  { label: "Downtown Milwaukee → ATW Appleton Airport",   pickup_address: "Downtown Milwaukee, WI",                                      dropoff_address: "Appleton International Airport (ATW), Appleton, WI" },
  { label: "Downtown Milwaukee → Lambeau Field, Green Bay", pickup_address: "Downtown Milwaukee, WI",                                    dropoff_address: "Lambeau Field, 1265 Lombardi Ave, Green Bay, WI" },
  { label: "MSN Madison Airport → Lambeau Field",         pickup_address: "Dane County Regional Airport (MSN), Madison, WI",            dropoff_address: "Lambeau Field, 1265 Lombardi Ave, Green Bay, WI" },
  { label: "MSN Madison Airport → ORD Chicago",           pickup_address: "Dane County Regional Airport (MSN), Madison, WI",            dropoff_address: "O'Hare International Airport (ORD), Chicago, IL" },
  { label: "Pewaukee → MKE Airport",                      pickup_address: "Pewaukee, WI",                                                dropoff_address: "Milwaukee Mitchell International Airport (MKE), Milwaukee, WI" },
  { label: "Waukesha → MKE Airport",                      pickup_address: "Waukesha, WI",                                                dropoff_address: "Milwaukee Mitchell International Airport (MKE), Milwaukee, WI" },
  { label: "Wauwatosa → MKE Airport",                     pickup_address: "Wauwatosa, WI",                                               dropoff_address: "Milwaukee Mitchell International Airport (MKE), Milwaukee, WI" },
  { label: "Pewaukee → ORD Chicago",                      pickup_address: "Pewaukee, WI",                                                dropoff_address: "O'Hare International Airport (ORD), Chicago, IL" },
  { label: "Waukesha → ORD Chicago",                      pickup_address: "Waukesha, WI",                                                dropoff_address: "O'Hare International Airport (ORD), Chicago, IL" },
  { label: "Wauwatosa → ORD Chicago",                     pickup_address: "Wauwatosa, WI",                                               dropoff_address: "O'Hare International Airport (ORD), Chicago, IL" },
];

/** Tiny button + customize modal — generates a branded PDF via POST /api/reports/rate-sheet. */
export default function RateSheetButton() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const generate = async (payload, fileName) => {
    setBusy(true);
    try {
      const r = await api.post("/reports/rate-sheet", payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "DeluxLimo-Rate-Sheet.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Rate sheet ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate");
    } finally {
      setBusy(false);
    }
  };

  const quickGenerate = () =>
    generate({
      title: "Major Destinations Rate Sheet",
      subtitle: `All-in prices including tax + card processing — ${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}`,
      tip_pcts: [0.15, 0.20],
      hourly_hours: [3],
      routes: DEFAULT_ROUTES,
    }, "DeluxLimo-Rate-Sheet.pdf");

  return (
    <>
      <div className="flex items-center gap-2" data-testid="rate-sheet-control">
        <button
          data-testid="rate-sheet-quick"
          onClick={quickGenerate}
          disabled={busy}
          className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] border border-[#D4AF37] text-[#D4AF37] flex items-center gap-2 hover:bg-[#D4AF37]/10 disabled:opacity-50"
          title="Generate the standard Major Destinations rate sheet PDF"
        >
          {busy ? <Loader2 size={12} className="animate-spin"/> : <FileText size={12}/>}
          Rate Sheet
        </button>
        <button
          data-testid="rate-sheet-customize"
          onClick={() => setOpen(true)}
          disabled={busy}
          className="text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-white"
        >customize</button>
      </div>

      {open && <CustomizeModal onClose={() => setOpen(false)} onGenerate={generate}/>}
    </>
  );
}

function CustomizeModal({ onClose, onGenerate }) {
  const [title, setTitle] = useState("Custom Rate Sheet");
  const [subtitle, setSubtitle] = useState("All-in prices including tax + card processing");
  const [routes, setRoutes] = useState(DEFAULT_ROUTES.slice(0, 5));
  const [tips, setTips] = useState({ "15": true, "20": true, "25": false });
  const [includeHourly, setIncludeHourly] = useState(true);
  const [hourlyHours, setHourlyHours] = useState(3);
  const [busy, setBusy] = useState(false);

  const updateRoute = (idx, key, val) => {
    setRoutes(rs => rs.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  };
  const addRoute = () => setRoutes(rs => [...rs, { label: "", pickup_address: "", dropoff_address: "" }]);
  const removeRoute = (idx) => setRoutes(rs => rs.filter((_, i) => i !== idx));

  const submit = async () => {
    const tip_pcts = Object.keys(tips).filter(k => tips[k]).map(k => Number(k) / 100);
    if (!tip_pcts.length) { toast.error("Pick at least one tip option"); return; }
    const cleanRoutes = routes.filter(r => r.pickup_address && r.dropoff_address && r.label);
    if (!cleanRoutes.length) { toast.error("Add at least one complete route"); return; }
    setBusy(true);
    await onGenerate({
      title, subtitle,
      tip_pcts,
      routes: cleanRoutes,
      hourly_hours: includeHourly ? [Number(hourlyHours)] : null,
    }, `DeluxLimo-${title.replace(/\s+/g, "-")}.pdf`);
    setBusy(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="rate-sheet-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-3xl w-full my-auto">
        <div className="px-6 py-5 border-b border-white/5 flex justify-between items-start">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Customize</div>
            <div className="font-serif text-2xl text-[#D4AF37] mt-1">Rate sheet</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="rs-close"><X size={18}/></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="field-label">Title</label>
              <input data-testid="rs-title" className="field-input" value={title} onChange={e => setTitle(e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Subtitle</label>
              <input data-testid="rs-subtitle" className="field-input" value={subtitle} onChange={e => setSubtitle(e.target.value)}/>
            </div>
          </div>

          <div>
            <label className="field-label">Tip presets to include</label>
            <div className="flex gap-2">
              {["15", "20", "25"].map(t => (
                <label key={t} className={`flex items-center gap-2 px-3 py-2 border text-xs cursor-pointer ${tips[t] ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-[#A1A1AA]"}`}>
                  <input type="checkbox" checked={tips[t]} onChange={e => setTips(p => ({...p, [t]: e.target.checked}))} className="accent-[#D4AF37]" data-testid={`tip-${t}`}/>
                  {t}% tip
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 mb-2 cursor-pointer">
              <input type="checkbox" data-testid="rs-hourly-toggle" checked={includeHourly} onChange={e => setIncludeHourly(e.target.checked)} className="accent-[#D4AF37]"/>
              <span className="text-sm text-[#F5F2EB]">Include hourly table</span>
            </label>
            {includeHourly && (
              <input
                data-testid="rs-hourly-hours"
                type="number" min="1" max="12"
                className="field-input w-32"
                value={hourlyHours} onChange={e => setHourlyHours(e.target.value)}
              />
            )}
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="field-label !mb-0">Routes ({routes.length})</label>
              <button data-testid="rs-add-route" onClick={addRoute} className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] flex items-center gap-1"><Plus size={11}/> Add route</button>
            </div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-2">
              {routes.map((r, i) => (
                <div key={i} className="border border-white/10 p-3 space-y-2" data-testid={`rs-route-${i}`}>
                  <div className="flex justify-between items-center">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Route {i + 1}</div>
                    <button onClick={() => removeRoute(i)} className="text-red-400" data-testid={`rs-rm-${i}`}><Trash2 size={12}/></button>
                  </div>
                  <input className="field-input" placeholder="Label (e.g. Downtown → MKE)" value={r.label} onChange={e => updateRoute(i, "label", e.target.value)}/>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input className="field-input" placeholder="Pickup address" value={r.pickup_address} onChange={e => updateRoute(i, "pickup_address", e.target.value)}/>
                    <input className="field-input" placeholder="Drop-off address" value={r.dropoff_address} onChange={e => updateRoute(i, "dropoff_address", e.target.value)}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-white/5 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
          <button data-testid="rs-generate" disabled={busy} onClick={submit} className="btn-gold text-[11px]">
            {busy ? "Generating…" : "Generate PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
